import { useState, useEffect, useRef, type RefObject } from 'react'
import type { Room, RoomCreate, RoomUpdate } from '../types'
import { addRoom, updateRoom } from '../api/projects'
import { Modal } from '../components/Modal'
import { Input } from '../components/Input'
import { Button } from '../components/Button'
import { Counter } from '../components/Counter'
import styles from './RoomEditor.module.css'

interface RoomEditorProps {
  projectId: string
  room: Room | null
  onSave: (room: Room) => void
  onClose: () => void
}

const ROOM_TYPES = [
  // Habitaciones
  { value: 'dormitorio',  label: 'Dormitorio',               ip44: false },
  { value: 'living',      label: 'Living / Estar',            ip44: false },
  // Servicios
  { value: 'cocina',      label: 'Cocina',                   ip44: false },
  { value: 'baño',        label: 'Baño',                     ip44: true  },
  { value: 'lavandería',  label: 'Lavandería / Logia',       ip44: true  },
  // Otros interiores
  { value: 'otro',        label: 'Pasillo / Otro interior',  ip44: false },
  // Exteriores con IP44
  { value: 'garage',      label: 'Garage',                   ip44: true  },
  { value: 'exterior',    label: 'Terraza / Patio / Exterior', ip44: true },
]

const SPECIAL_CIRCUITS = [
  { key: 'lavadora', label: 'Lavadora (circuito 16A + enchufe IP44)' },
  { key: 'secadora', label: 'Secadora (circuito 16A + enchufe IP44)' },
  { key: 'horno_electrico', label: 'Horno eléctrico (circuito 16A + enchufe IP44)' },
  { key: 'aire_acondicionado', label: 'Aire acondicionado (circuito 16A)' },
  { key: 'cargador_auto', label: 'Cargador auto eléctrico (circuito 20A)' },
]

function scrollToFirst(ref: RefObject<HTMLElement | null>) {
  if (ref.current?.scrollIntoView) {
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  ref.current?.focus()
}

export function RoomEditor({ projectId, room, onSave, onClose }: RoomEditorProps) {
  const isEditing = room !== null
  const [loading, setLoading] = useState(false)
  const [showSpecial, setShowSpecial] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [form, setForm] = useState({
    name: '',
    type: '',
    width_cm: '',
    height_cm: '',
    floor: 1,
    light_points: 0,
    outlet_points: 0,
    special_circuits: [] as string[],
  })

  const nameRef = useRef<HTMLInputElement>(null)
  const widthRef = useRef<HTMLInputElement>(null)
  const heightRef = useRef<HTMLInputElement>(null)
  const typeSelectRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    if (room) {
      setForm({
        name: room.name,
        type: room.type,
        width_cm: String(room.width_cm),
        height_cm: String(room.height_cm),
        floor: room.floor,
        light_points: room.light_points,
        outlet_points: room.outlet_points,
        special_circuits: Array.isArray(room.special_circuits)
          ? (room.special_circuits as string[])
          : [],
      })
    }
  }, [room])

  const set = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const toggleSpecial = (key: string) => {
    setForm((prev) => ({
      ...prev,
      special_circuits: prev.special_circuits.includes(key)
        ? prev.special_circuits.filter((k) => k !== key)
        : [...prev.special_circuits, key],
    }))
  }

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'El nombre del recinto es obligatorio.'
    if (!form.type) errs.type = 'Selecciona el tipo de recinto.'
    const w = Number(form.width_cm)
    const h = Number(form.height_cm)
    if (!form.width_cm || w <= 0) errs.width_cm = 'El ancho debe ser un número mayor a 0.'
    if (!form.height_cm || h <= 0) errs.height_cm = 'El largo debe ser un número mayor a 0.'
    if (form.light_points === 0 && form.outlet_points === 0) {
      errs.points = 'El recinto debe tener al menos un punto eléctrico.'
    }
    setErrors(errs)

    if (errs.name) { scrollToFirst(nameRef); return false }
    if (errs.type) { scrollToFirst(typeSelectRef); return false }
    if (errs.width_cm) { scrollToFirst(widthRef); return false }
    if (errs.height_cm) { scrollToFirst(heightRef); return false }

    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const payload: RoomCreate | RoomUpdate = {
        name: form.name.trim(),
        type: form.type,
        width_cm: Number(form.width_cm),
        height_cm: Number(form.height_cm),
        floor: form.floor,
        light_points: form.light_points,
        outlet_points: form.outlet_points,
        special_circuits: form.special_circuits,
      }
      const saved = isEditing
        ? await updateRoom(projectId, room.id, payload as RoomUpdate)
        : await addRoom(projectId, payload as RoomCreate)
      onSave(saved)
    } catch {
      setErrors({ form: 'No pudimos guardar el recinto. Intenta nuevamente.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? 'Editar recinto' : 'Agregar recinto'}
    >
      <form onSubmit={handleSubmit} className={styles.form} noValidate>
        <Input
          label="Nombre del recinto"
          name="name"
          value={form.name}
          onChange={set('name')}
          placeholder="Dormitorio 1"
          error={errors.name}
          required
          ref={nameRef}
        />

        <div className={styles.field}>
          <label className={styles.label}>
            Tipo de recinto <span className={styles.req}>*</span>
          </label>
          <select
            className={[styles.select, errors.type ? styles.selectError : ''].join(' ')}
            value={form.type}
            onChange={set('type')}
            ref={typeSelectRef}
          >
            <option value="">Selecciona...</option>
            {ROOM_TYPES.map(({ value, label, ip44 }) => (
              <option key={value} value={value}>
                {label}{ip44 ? ' · IP44' : ''}
              </option>
            ))}
          </select>
          {errors.type && <span className={styles.error}>{errors.type}</span>}
          {ROOM_TYPES.find((rt) => rt.value === form.type)?.ip44 && (
            <p className={styles.ip44Note}>
              ℹ️ Los enchufes de este recinto tendrán protección IP44 automáticamente (RIC N°11)
            </p>
          )}
        </div>

        <div className={styles.sectionTitle}>Dimensiones</div>
        <p className={styles.hint}>Ingresa las dimensiones en centímetros</p>
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>Ancho <span className={styles.req}>*</span></label>
            <div className={styles.withSuffix}>
              <input
                className={[styles.input, errors.width_cm ? styles.inputError : ''].join(' ')}
                type="number"
                name="width_cm"
                value={form.width_cm}
                onChange={set('width_cm')}
                placeholder="300"
                min="1"
                ref={widthRef}
              />
              <span className={styles.suffix}>cm</span>
            </div>
            {errors.width_cm && <span className={styles.error}>{errors.width_cm}</span>}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Largo <span className={styles.req}>*</span></label>
            <div className={styles.withSuffix}>
              <input
                className={[styles.input, errors.height_cm ? styles.inputError : ''].join(' ')}
                type="number"
                name="height_cm"
                value={form.height_cm}
                onChange={set('height_cm')}
                placeholder="400"
                min="1"
                ref={heightRef}
              />
              <span className={styles.suffix}>cm</span>
            </div>
            {errors.height_cm && <span className={styles.error}>{errors.height_cm}</span>}
          </div>
        </div>

        <div className={styles.sectionTitle}>Puntos eléctricos</div>
        <Counter
          label="Puntos de iluminación"
          value={form.light_points}
          onChange={(v) => setForm((p) => ({ ...p, light_points: v }))}
        />
        <Counter
          label="Enchufes"
          value={form.outlet_points}
          onChange={(v) => setForm((p) => ({ ...p, outlet_points: v }))}
        />
        {errors.points && (
          <p className={styles.error} role="alert">{errors.points}</p>
        )}

        <button
          type="button"
          className={styles.toggleSpecial}
          onClick={() => setShowSpecial((s) => !s)}
          aria-expanded={showSpecial}
        >
          {showSpecial ? '▲' : '▼'} Circuitos especiales
          {form.special_circuits.length > 0 && (
            <span className={styles.specialCount}>{form.special_circuits.length}</span>
          )}
        </button>

        {showSpecial && (
          <div className={styles.specialList}>
            <p className={styles.specialNote}>
              ℹ️ Cada artefacto especial genera un circuito exclusivo con su enchufe IP44 incluido
              (RIC N°10 5.2.1d). No agregues enchufes adicionales para estos artefactos.
            </p>
            {SPECIAL_CIRCUITS.map(({ key, label }) => (
              <label key={key} className={styles.checkLabel}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={form.special_circuits.includes(key)}
                  onChange={() => toggleSpecial(key)}
                />
                {label}
              </label>
            ))}
          </div>
        )}

        {errors.form && (
          <p className={styles.error} role="alert">{errors.form}</p>
        )}

        <div className={styles.actions}>
          <Button type="submit" loading={loading} fullWidth>
            Guardar recinto
          </Button>
          <Button variant="secondary" onClick={onClose} fullWidth>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  )
}
